<template>
  <div>
    <CToaster :autohide="3000" placement="top-end">
      <template v-for="info in infoList">
        <CToast :key="info.id" :show="true" :header="info.header" :color="info.color">
          {{ info.message }}
        </CToast>
      </template>
    </CToaster>

    <!-- Page header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="8">
            <h1 class="h4 mb-1 font-weight-bold">AI assistant</h1>
            <p class="text-medium-emphasis mb-0">
              Let Claude create draft invoices for you when you ask in chat
            </p>
          </CCol>
          <CCol md="4" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" :disabled="loading" @click="load">
              <CIcon name="cil-reload" /> Refresh
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- First load -->
    <CCard v-if="loading && !settings" class="border-0 shadow-sm">
      <CCardBody class="text-center py-5" role="status">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading AI assistant settings…</p>
      </CCardBody>
    </CCard>

    <!-- Error -->
    <CCard v-else-if="loadError && !settings" class="border-0 shadow-sm">
      <CCardBody class="text-center py-5">
        <CIcon name="cil-warning" size="xl" class="text-warning mb-3" />
        <h2 class="h5">Couldn't load AI assistant settings</h2>
        <p class="text-muted">Check your connection and try again.</p>
        <CButton color="primary" @click="load">Try again</CButton>
      </CCardBody>
    </CCard>

    <template v-else-if="settings">
      <CAlert v-if="!settings.featureAvailable" color="warning">
        The AI assistant isn't available on Niagaku yet. You'll be able to connect Claude here once it is.
      </CAlert>

      <CAlert v-else-if="!settings.isOwner" color="info">
        Only the business owner can manage the AI assistant for {{ settings.businessName }}.
      </CAlert>

      <template v-else>
        <!-- Access switch -->
        <CCard class="border-0 shadow-sm">
          <CCardBody class="p-4">
            <div class="d-flex align-items-start justify-content-between">
              <div class="mr-3">
                <label for="agent-access" class="h5 d-block mb-1">
                  Allow the AI assistant for {{ settings.businessName }}
                </label>
                <p class="text-muted mb-0">
                  Claude can look up your customers, products and invoices, and create draft invoices.
                  It can't approve, send, delete or record payments — you do that here.
                </p>
              </div>
              <CSwitch
                id="agent-access"
                color="primary"
                shape="pill"
                class="mt-1 flex-shrink-0"
                :checked="settings.enabled"
                :disabled="saving"
                @update:checked="setEnabled"
              />
            </div>
          </CCardBody>
        </CCard>

        <!-- How to connect -->
        <CCard class="border-0 shadow-sm">
          <CCardBody class="p-4">
            <h2 class="h5 mb-3">Connect Claude</h2>
            <ol class="pl-3 mb-3">
              <li class="mb-2">In Claude, open <strong>Settings → Connectors</strong> and choose <strong>Add custom connector</strong>.</li>
              <li class="mb-2">
                Paste this address:
                <div class="d-flex align-items-center mt-2">
                  <code class="text-monospace bg-light rounded px-2 py-1 mr-2 text-break">{{ settings.mcpUrl }}</code>
                  <CButton color="light" size="sm" aria-label="Copy connector address" @click="copyUrl">
                    <CIcon name="cil-clipboard" /> Copy
                  </CButton>
                </div>
              </li>
              <li>Sign in to Niagaku when Claude asks, and choose this business.</li>
            </ol>
            <p class="text-muted small mb-0">Custom connectors need a Claude Pro, Max, Team or Enterprise plan.</p>
          </CCardBody>
        </CCard>

        <!-- Connected apps -->
        <CCard class="border-0 shadow-sm">
          <CCardBody class="p-4">
            <h2 class="h5 mb-3">Connected apps</h2>
            <div v-if="!settings.connections.length" class="text-center py-3">
              <CIcon name="cil-speech" size="xl" class="text-muted mb-2" />
              <p class="font-weight-bold mb-1">No apps connected yet</p>
              <p class="text-muted mb-0">Follow the steps above to connect Claude.</p>
            </div>
            <div
              v-for="c in settings.connections"
              v-else
              :key="c.id"
              class="d-flex align-items-center justify-content-between border-top py-3"
            >
              <div class="mr-3">
                <div class="font-weight-bold">{{ c.clientName }}</div>
                <div class="text-muted small">Connected {{ formatDateTime(c.createdOn) }}</div>
              </div>
              <CButton color="danger" variant="outline" @click="askDisconnect(c)">Disconnect</CButton>
            </div>
          </CCardBody>
        </CCard>

        <!-- Activity -->
        <CCard class="border-0 shadow-sm">
          <CCardBody class="p-4">
            <h2 class="h5 mb-3">Recent activity</h2>

            <div v-if="activityError" class="text-center py-3">
              <p class="text-muted mb-2">Couldn't load recent activity.</p>
              <CButton color="primary" size="sm" @click="loadActivity">Try again</CButton>
            </div>
            <div v-else-if="!activityLoading && !activity.items.length" class="text-center py-3">
              <p class="font-weight-bold mb-1">Nothing yet</p>
              <p class="text-muted mb-0">When Claude creates a draft invoice, it shows up here.</p>
            </div>
            <template v-else>
              <CProgress v-if="activityLoading" :value="100" animated size="xs" class="mb-2" />

              <!-- Desktop -->
              <div class="d-none d-lg-block">
                <table class="table mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>Invoice</th>
                      <th class="text-right">Total</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="a in activity.items" :key="a.id">
                      <td>{{ formatDateTime(a.createdOn) }}</td>
                      <td>{{ actionLabel(a.tool) }}</td>
                      <td>
                        <router-link v-if="a.invoiceNumber" :to="'/tenants/Invoice/' + a.entityId" class="text-monospace">
                          {{ a.invoiceNumber }}
                        </router-link>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td class="text-right">{{ a.grandTotal != null ? 'RM ' + formatMyr(a.grandTotal) : '—' }}</td>
                      <td>
                        <CBadge :color="a.outcome === 'success' ? 'success' : 'danger'">
                          {{ a.outcome === 'success' ? 'Created' : 'Not created' }}
                        </CBadge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile -->
              <div class="d-lg-none">
                <div v-for="a in activity.items" :key="a.id" class="border-top py-3">
                  <div class="d-flex justify-content-between">
                    <span class="font-weight-bold">{{ actionLabel(a.tool) }}</span>
                    <CBadge :color="a.outcome === 'success' ? 'success' : 'danger'">
                      {{ a.outcome === 'success' ? 'Created' : 'Not created' }}
                    </CBadge>
                  </div>
                  <div class="text-muted small">{{ formatDateTime(a.createdOn) }}</div>
                  <div v-if="a.invoiceNumber" class="d-flex justify-content-between mt-1">
                    <router-link :to="'/tenants/Invoice/' + a.entityId" class="text-monospace">{{ a.invoiceNumber }}</router-link>
                    <span>RM {{ formatMyr(a.grandTotal) }}</span>
                  </div>
                </div>
              </div>

              <CPagination
                v-if="activityPages > 1"
                class="mt-3 mb-0"
                :active-page.sync="activityPage"
                :pages="activityPages"
                align="end"
              />
            </template>
          </CCardBody>
        </CCard>
      </template>
    </template>

    <!-- Disconnect confirm -->
    <CModal :title="'Disconnect ' + (pending ? pending.clientName : '') + '?'" :show.sync="confirmOpen" :close-on-backdrop="!disconnecting">
      <p class="mb-0">
        {{ pending && pending.clientName }} will lose access to {{ settings && settings.businessName }} straight away.
        Invoices it already created stay in Niagaku. You can connect it again at any time.
      </p>
      <template #footer>
        <CButton color="light" :disabled="disconnecting" @click="confirmOpen = false">Keep it</CButton>
        <CButton color="danger" :disabled="disconnecting" @click="disconnect">
          <CSpinner v-if="disconnecting" size="sm" class="mr-1" /> Disconnect
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import AgentSettingsApi from "@/lib/agentSettingsApi";
import { formatDateTime } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

const PAGE_SIZE = 10;

export default {
  name: "AgentSettings",
  data() {
    return {
      api: new AgentSettingsApi(),
      settings: null,
      loading: false,
      loadError: false,
      saving: false,
      activity: { items: [], totalCount: 0 },
      activityPage: 1,
      activityLoading: false,
      activityError: false,
      confirmOpen: false,
      pending: null,
      disconnecting: false,
      infoList: [],
      toastId: 0,
    };
  },
  computed: {
    activityPages() {
      return Math.ceil(this.activity.totalCount / PAGE_SIZE);
    },
  },
  watch: {
    activityPage() {
      this.loadActivity();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDateTime,
    formatMyr,
    load() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getCurrentBusiness()
        .then((response) => {
          this.settings = response.result;
          if (this.settings.isOwner && this.settings.featureAvailable) this.loadActivity();
        })
        .catch((error) => {
          console.error("Failed to load AI assistant settings", error);
          this.loadError = true;
          if (this.settings) this.toast("Error", "Couldn't refresh. Check your connection and try again", "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadActivity() {
      this.activityLoading = true;
      this.activityError = false;
      this.api
        .getActivity(this.activityPage, PAGE_SIZE)
        .then((response) => {
          this.activity = response.result;
        })
        .catch((error) => {
          console.error("Failed to load AI activity", error);
          this.activityError = true;
        })
        .finally(() => {
          this.activityLoading = false;
        });
    },
    setEnabled(enabled) {
      this.saving = true;
      this.api
        .setEnabled(enabled)
        .then(() => {
          this.settings.enabled = enabled;
          this.toast("Saved", enabled ? "AI assistant turned on" : "AI assistant turned off", "success");
        })
        .catch((error) => {
          console.error("Failed to change AI access", error);
          this.toast("Error", "Couldn't change AI access. Try again", "danger");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    copyUrl() {
      navigator.clipboard
        .writeText(this.settings.mcpUrl)
        .then(() => this.toast("Copied", "Connector address copied", "success"))
        .catch(() => this.toast("Error", "Couldn't copy. Select the address and copy it yourself", "danger"));
    },
    actionLabel(tool) {
      return tool === "create_draft_invoice" ? "Create draft invoice" : "AI action";
    },
    askDisconnect(connection) {
      this.pending = connection;
      this.confirmOpen = true;
    },
    disconnect() {
      this.disconnecting = true;
      this.api
        .disconnect(this.pending.id)
        .then(() => {
          this.settings.connections = this.settings.connections.filter((c) => c.id !== this.pending.id);
          this.toast("Disconnected", `${this.pending.clientName} disconnected`, "success");
          this.confirmOpen = false;
        })
        .catch((error) => {
          console.error("Failed to disconnect", error);
          this.toast("Error", "Couldn't disconnect. Try again", "danger");
        })
        .finally(() => {
          this.disconnecting = false;
        });
    },
    toast(header, message, color) {
      this.infoList.push({ id: ++this.toastId, header, message, color });
    },
  },
};
</script>
